<template>
  <div>
    <b-card>
      <breadcrumbs
        :breadcrumb-items="breadcrumbItems"
      />
      <DspPartnerToolbar
        v-if="Object.keys(dsppartner).length !== 0"
        :dsppartner="dsppartner"
        closing
      />
    </b-card>
    <StatsNavigation />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-top">
            <h4>{{ $t('stats.campaign_stats') }}</h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              :value="rangePicker"
              :is-loading="isLoading"
              @refresh="handleRefresh"
              @input="toggleRange"
            >
              <template #additional-filters>
                <div class="search-input ml-2">
                  <b-input-group>
                    <b-form-input
                      v-model="searchQuery"
                      :placeholder="$t('form.search')"
                      @input="onSearch"
                    />
                    <b-input-group-append>
                      <b-button
                        variant="outline-primary"
                        @click="onSearch"
                      >
                        <feather-icon
                          icon="SearchIcon"
                        />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>
              </template>
            </StatsDateRangeFilter>
          </div>
        </div>
      </div>
      <div
        v-if="isLoading"
        class="text-center my-3"
      >
        <b-spinner label="Loading..." />
      </div>
      <b-table
        v-else-if="rows.length"
        :items="rows"
        :fields="fields"
        striped
        responsive
        small
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        show-empty
        foot-clone
        class="text-center"
      >
        <template #head(campaign)>
          {{ $t('stats.campaign') }}
        </template>
        <template #head(impressions)>
          {{ $t('stats.impressions') }}
        </template>
        <template #head(click)>
          {{ $t('stats.clicks') }}
        </template>
        <template #head(revenue)>
          {{ $t('stats.revenue') }}
        </template>
        <template #cell(campaign)="{ item }">
          <router-link :to="{ name: 'dashboard-dsppartner-stats-campaign-details', params: { campaignId: item.campaign_id }}">
            {{ campaignNameByIdFromStore(item.campaign_id) || item.campaign_id }}
          </router-link>
        </template>
        <template #cell(impressions)="data">
          {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
        </template>
        <template #cell(click)="data">
          {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
        </template>
        <template #cell(revenue)="data">
          {{ data.value ? currency('USD', data.value) : 0 }}
        </template>
        <template #foot(campaign)="data">
          <strong :data-label="data.label">{{ $t('Records') }}: {{ rows.length }}</strong>
        </template>
        <template #foot(impressions)>
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('impressions')) }}
        </template>
        <template #foot(click)>
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('click')) }}
        </template>
        <template #foot(revenue)>
          {{ currency('USD', sum('revenue')) }}
        </template>
      </b-table>
      <div
        v-else
        class="text-center text-muted my-3"
      >
        {{ $t('No data for this period') }}
      </div>
    </b-card>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import currencyFormatter from '@/utils/currency-formatter'
import getFormattedDate from '@/utils/date-formatter'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import {
  BCard,
  BTable,
  BButton,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { mapGetters } from 'vuex'
import DspPartnerToolbar from '../DspPartnerToolbar.vue'
import StatsNavigation from './StatsNavigation.vue'
import StatsDateRangeFilter from './StatsDateRangeFilter.vue'

export default {
  name: 'CampaignStats',
  components: {
    BCard,
    BTable,
    BButton,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BSpinner,
    Breadcrumbs,
    DspPartnerToolbar,
    StatsNavigation,
    StatsDateRangeFilter,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      currency: currencyFormatter,
      rangePicker: [getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), getFormattedDate(new Date())],
      getFormattedDate,
      rows: [],
      isLoading: false,
      searchQuery: '',
      sortBy: 'impressions',
      sortDesc: true,
      sortDirection: 'desc',
      fields: [
        { key: 'campaign', label: 'Кампейн', sortable: true },
        { key: 'impressions', label: 'Impressions', sortable: true },
        { key: 'click', label: 'Click', sortable: true },
        { key: 'revenue', label: 'Revenue', sortable: true },
      ],
      breadcrumbItems: [],
      dsppartner: {},
    }
  },
  computed: {
    ...mapGetters('dashboard-campaings-stats', ['campaignNameByIdFromStore']),
    selectedRangeFormated() {
      let dateFrom
      let dateTo
      const selectedRange = this.rangePicker
      if (Array.isArray(selectedRange)) {
        [dateFrom, dateTo] = selectedRange
      } else {
        [dateFrom, , dateTo] = selectedRange.split(' ')
      }

      return {
        from: getFormattedDate(new Date(dateFrom)),
        to: dateTo === undefined ? getFormattedDate(new Date(dateFrom)) : getFormattedDate(new Date(dateTo)),
      }
    },
  },
  created() {
    this.getPartnerData()

    // Priority: URL params > saved settings > default values
    const { from, to } = this.$route.query
    if (from && to) {
      // If URL has params, use them
      this.rangePicker = [from, to]
      this.fetchCampaignStats() // Call only once during initialization
    } else {
      // Otherwise try to load saved settings
      this.loadSavedSettings()
      this.fetchCampaignStats() // Call only once during initialization
    }
  },
  methods: {
    async getPartnerData() {
      const responseData = await useJwt.getDsppartner(this.$route.params.id)
      this.dsppartner = responseData.data.dsppartner || {}
      this.setBreabcrumbs()
    },
    setBreabcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$t('DSP partners'),
          to: '/dsppartners/',
        },
        {
          text: this.dsppartner.name,
          to: `/dsppartners/${this.dsppartner._id}/`,
        },
        {
          text: this.$t('Statistics'),
          active: true,
        },
      ]
    },
    async fetchCampaignStats() {
      this.isLoading = true
      try {
        const params = {
          partner_id: this.$route.params.id,
          from: this.selectedRangeFormated.from,
          to: this.selectedRangeFormated.to,
          type: 'daily',
        }
        const response = await useJwt.getPhoenixDspCampaignTotal(params)

        if (response.data && response.data.items) {
          this.rows = response.data.items.map(campaign => ({
            campaign_id: campaign.campaign_id,
            impressions: campaign.impressions || 0,
            click: campaign.clicks || 0,
            revenue: campaign.revenue || 0,
          }))

          // Collect all unique campaign_id and load names
          const ids = [...new Set(this.rows.map(item => item.campaign_id).filter(Boolean))]
          if (ids.length) {
            await this.$store.dispatch('dashboard-campaings-stats/fetchCampaignNamesById', ids)
          }
        } else {
          this.rows = []
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Data Error',
            msg: 'Failed to load campaign statistics',
            variant: 'danger',
          },
          { root: true },
        )
        this.rows = []
      } finally {
        this.isLoading = false
      }
    },
    sum(key) {
      return this.rows.reduce((acc, r) => acc + (r[key] || 0), 0)
    },
    toggleRange(selectedDates) {
      if (!selectedDates || !Array.isArray(selectedDates) || selectedDates.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        return
      }

      const [fromDate, toDate] = selectedDates
      const from = getFormattedDate(new Date(fromDate))
      const to = getFormattedDate(new Date(toDate))

      // Only update if we have valid dates
      if (from !== 'Invalid date' && to !== 'Invalid date') {
        // Check if dates actually changed
        if (this.rangePicker[0] === from && this.rangePicker[1] === to) {
          return
        }

        // Update state and URL in one go
        this.rangePicker = [from, to]
        // Update URL without page reload
        const newQuery = { ...this.$route.query, from, to }
        const newPath = this.$route.path
        window.history.replaceState(
          {},
          '',
          `${newPath}?${new URLSearchParams(newQuery).toString()}`,
        )
        this.saveSettings()
        this.fetchCampaignStats()
      } else {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Format Error',
            msg: 'Invalid date format',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    onSearch() {
      if (!this.searchQuery) {
        this.fetchCampaignStats()
        return
      }
      const query = this.searchQuery.toLowerCase()
      this.rows = this.rows.filter(item => {
        const campaignName = this.campaignNameByIdFromStore(item.campaign_id) || ''
        return campaignName.toLowerCase().includes(query) || item.campaign_id.toLowerCase().includes(query)
      })
    },
    handleRefresh() {
      this.fetchCampaignStats()
    },
    saveSettings() {
      try {
        localStorage.setItem('dspStatsDateFilter', JSON.stringify({ rangePicker: this.rangePicker }))
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Settings Error',
            msg: 'Failed to save date filter settings',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    loadSavedSettings() {
      try {
        const saved = localStorage.getItem('dspStatsDateFilter')
        if (saved) {
          const settings = JSON.parse(saved)
          if (settings.rangePicker && !this.$route.query.from && !this.$route.query.to) {
            this.rangePicker = settings.rangePicker
          }
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Settings Error',
            msg: 'Failed to load date filter settings',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style scoped lang="scss">
.card-statistics .card-body {
  min-height: 190px;
}

.card-statistics-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.search-input {
  min-width: 200px;
}

@media (max-width: 440px) {
  [dir] #app {
    .card-statistics-tools {
      flex-wrap: wrap;
    }
  }
}
</style>
